import { openBimIndexLease } from "@novorender/api/bim-index-lease";

import { INDEX_FILES, type IndexFile } from "./protocol";

/**
 * Whether a scene's BIM index is on disk, and complete enough to serve the data access layer. A
 * synced scene is no promise of one: a sync may find no index to download, and the SDK skips files
 * it can't store safely.
 */

/**
 * Sections `BimIndexOfflineDataAccess` can answer nothing without: the engine won't open without
 * `manifest.json`, `object_tree.idx` serves every hierarchy call, and `properties.idx` every
 * property query and path lookup. Each other section costs one capability.
 */
const REQUIRED_SECTIONS: readonly IndexFile[] = ["manifest.json", "object_tree.idx", "properties.idx"];

/** What a {@link probeBimIndex} found. */
export interface BimIndexAvailability {
    sceneDirName: string;
    /** The sections that are there, in {@link INDEX_FILES} order. */
    present: IndexFile[];
    /** The {@link REQUIRED_SECTIONS} that are not. Empty means usable. */
    missing: IndexFile[];
    /** Whether the data access layer can be backed by what is on disk. */
    usable: boolean;
}

/**
 * Which of the index's sections the scene has, and whether that is enough to use it. Looks up
 * handles only, so a truncated or corrupt section counts as present and fails at `open`; and a
 * sync or delete may change what the later open finds. Keep the fallback to the object DB.
 * @throws What reading the scene's sync state record failed with.
 */
export async function probeBimIndex(sceneDirName: string): Promise<BimIndexAvailability> {
    const present = await presentSections(sceneDirName);
    const missing = REQUIRED_SECTIONS.filter((name) => !present.includes(name));
    return { sceneDirName, present, missing, usable: missing.length === 0 };
}

async function presentSections(sceneDirName: string): Promise<IndexFile[]> {
    const lease = await openBimIndexLease(sceneDirName);
    try {
        if (!lease.dir) {
            return [];
        }
        const present: IndexFile[] = [];
        for (const name of INDEX_FILES) {
            if (await hasFile(lease.dir, name)) {
                present.push(name);
            }
        }
        return present;
    } finally {
        // Held only while looking: a delete of the scene waits for it.
        await lease.release();
    }
}

async function hasFile(dir: FileSystemDirectoryHandle, name: IndexFile): Promise<boolean> {
    try {
        await dir.getFileHandle(name);
        return true;
    } catch {
        return false;
    }
}
